import React, { useState } from "react";
import { Mail, Send, Users, MessageSquare } from "lucide-react";
import Card from "../../../components/ui/Card";
import { mockCreator } from "../../../data/mockData";
import BroadcastComposer from "../components/messages/BroadcastComposer";
import ContactList from "../components/engagement/ContactList";
import ChatWindow from "../components/engagement/ChatWindow";
import FanProfileSidebar from "../components/engagement/FanProfileSidebar";

const EngagementHubPage = () => {
  const [activeTab, setActiveTab] = useState("messages");
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  const selectedConversation = mockCreator.conversations.find(
    (c) => c.id === selectedConversationId
  );

  const handleSendMessage = (text) => {
    console.log("Sending message:", text);
    // In a real app, this would update state/backend
  };

  // 1. Determine active view state for mobile transitions
  const isChatOpen = activeTab === "messages" && selectedConversationId;

  return (
    // ✨ Container Query Wrapper
    <div className="@container h-[calc(100vh-6rem)] sm:h-[calc(100vh-8rem)] bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col sm:flex-row">
      
      {/* 2. Responsive Navigation: Left Rail (Desktop) / Bottom Bar (Mobile) */}
      <nav className="order-2 sm:order-1 sm:w-20 bg-white sm:bg-slate-50 border-t sm:border-t-0 sm:border-r border-slate-200 flex sm:flex-col items-center justify-around sm:justify-start py-2 sm:py-6 gap-2 sm:gap-6 z-10 shrink-0">
        <button
            onClick={() => { setActiveTab("messages"); setSelectedConversationId(null); }}
            className={`p-3 rounded-2xl transition-all flex flex-col items-center gap-1 ${activeTab === 'messages' ? 'text-purple-600 bg-purple-50 sm:bg-purple-600 sm:text-white shadow-sm' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
            title="Inbox"
        >
            <MessageSquare size={24} className="sm:w-5 sm:h-5" />
            <span className="text-[10px] font-bold sm:hidden">Inbox</span>
        </button>
        <button
            onClick={() => setActiveTab("broadcast")}
            className={`p-3 rounded-2xl transition-all flex flex-col items-center gap-1 ${activeTab === 'broadcast' ? 'text-purple-600 bg-purple-50 sm:bg-purple-600 sm:text-white shadow-sm' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
            title="Broadcasts"
        >
            <Send size={24} className="sm:w-5 sm:h-5" />
            <span className="text-[10px] font-bold sm:hidden">Campaigns</span>
        </button>
        <div className="hidden sm:block w-8 h-px bg-slate-200 my-2"></div>
        <button className="p-3 rounded-2xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex flex-col items-center gap-1" title="Groups">
            <Users size={24} className="sm:w-5 sm:h-5" />
            <span className="text-[10px] font-bold sm:hidden">Groups</span>
        </button>
      </nav>

      {/* 3. Main Content Area */}
      <div className="order-1 sm:order-2 flex-1 flex overflow-hidden relative">
          {activeTab === "messages" ? (
              <div className="flex-1 flex w-full relative">
                  {/* Pane 1: Contact List (Hidden on mobile if chat is open) */}
                  <div className={`absolute inset-0 z-10 bg-white sm:static flex-none w-full sm:w-80 border-r border-slate-200 transition-transform duration-300 ${isChatOpen ? '-translate-x-full sm:translate-x-0' : 'translate-x-0'}`}>
                      <ContactList
                          conversations={mockCreator.conversations}
                          activeConversationId={selectedConversationId}
                          onSelectConversation={setSelectedConversationId}
                      />
                  </div>

                  {/* Pane 2: Chat Window (Slide in on mobile) */}
                  <div className={`absolute inset-0 z-20 bg-white sm:static flex-1 flex flex-col min-w-0 transition-transform duration-300 ${isChatOpen ? 'translate-x-0' : 'translate-x-full sm:translate-x-0'}`}>
                      {selectedConversation ? (
                          <ChatWindow
                              conversation={selectedConversation}
                              onSendMessage={handleSendMessage}
                              onBack={() => setSelectedConversationId(null)}
                          />
                      ) : (
                          <div className="hidden sm:flex h-full items-center justify-center bg-slate-50/50">
                              <div className="text-center text-slate-400">
                                  <MessageSquare size={48} className="mx-auto mb-4 opacity-10" />
                                  <p className="font-medium">Select a conversation</p>
                              </div>
                          </div>
                      )}
                  </div>

                  {/* Pane 3: Fan Profile (Right Sidebar - Desktop Only) */}
                  {selectedConversation && (
                    <div className="hidden @3xl:block w-80 flex-none border-l border-slate-200 bg-white">
                        <FanProfileSidebar conversation={selectedConversation} />
                    </div>
                  )}
              </div>
          ) : (
              <div className="flex-1 overflow-y-auto bg-slate-50 p-2 sm:p-8 w-full">
                  <div className="mx-auto bg-white p-4 sm:p-8 rounded-3xl shadow-sm border border-slate-100">
                      <h2 className="text-2xl font-bold text-slate-800 mb-6">Create Broadcast</h2>
                      <BroadcastComposer />
                  </div>
              </div>
          )}
      </div>
    </div>
  );
};

export default EngagementHubPage;
